<div class="container-fluid">
    <h1 class="m-3 display-6 vm-container-header"><i class="bi bi-journal-text me-2"></i> Audit Log</h1>

    <div class="pb-5">
        <div class="m-3 p-3 bg-body-light rounded-3 shadow">
            <form [formGroup]="form" (ngSubmit)="search()" autocomplete="off">
                <div class="row g-2">
                    <div class="col-12 col-sm-6 col-lg-3">
                        <div class="form-floating">
                            <select class="form-select" id="customerId" formControlName="customerId">
                                <option value="">All customers</option>
                                <option [value]="vineMedsAdminCustomerId">VineMeds (admin)</option>
                                @for (customer of customers; track customer.id) {
                                    <option [value]="customer.id">{{ customer.name }}</option>
                                }
                            </select>
                            <label for="customerId">Customer</label>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-lg-3">
                        <div class="form-floating">
                            <select class="form-select" id="cognitoUserId" formControlName="cognitoUserId">
                                <option value="">Anyone</option>
                                @for (user of users; track user.cognitoUserId) {
                                    <option [value]="user.cognitoUserId">{{ user.name }}</option>
                                }
                            </select>
                            <label for="cognitoUserId">Changed by</label>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-lg-3">
                        <div class="form-floating">
                            <select class="form-select" id="tableName" formControlName="tableName">
                                <option value="">All tables</option>
                                @for (table of tables; track table) {
                                    <option [value]="table">{{ table }}</option>
                                }
                            </select>
                            <label for="tableName">Table</label>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-lg-3">
                        <div class="form-floating">
                            <input class="form-control" id="value" formControlName="value" type="text" placeholder="Value (old or new)">
                            <label for="value">Value (old or new)</label>
                        </div>
                        <div class="form-check form-switch mt-1">
                            <input class="form-check-input" type="checkbox" role="switch" id="contains" formControlName="contains">
                            <label class="form-check-label small" for="contains">Contains (exact match when off)</label>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-lg-3">
                        <div class="input-group">
                            <div class="form-floating">
                                <input class="form-control" id="from" formControlName="from"
                                       ngbDatepicker #from="ngbDatepicker" (click)="from.toggle()">
                                <label for="from">From</label>
                            </div>
                            <i class="bi bi-calendar vm-icon input-group-text" (click)="from.toggle()"></i>
                        </div>
                        @if (isControlInvalid(form.controls.from)) {
                            <div class="text-danger small mt-1">Enter a valid date.</div>
                        }
                    </div>
                    <div class="col-12 col-sm-6 col-lg-3">
                        <div class="input-group">
                            <div class="form-floating">
                                <input class="form-control" id="to" formControlName="to"
                                       ngbDatepicker #to="ngbDatepicker" (click)="to.toggle()">
                                <label for="to">To</label>
                            </div>
                            <i class="bi bi-calendar vm-icon input-group-text" (click)="to.toggle()"></i>
                        </div>
                        @if (isControlInvalid(form.controls.to)) {
                            <div class="text-danger small mt-1">Enter a valid date.</div>
                        }
                    </div>
                    <div class="col-12 col-lg-6 d-flex justify-content-end align-items-start gap-2">
                        <button type="button" class="btn btn-outline-primary" (click)="reset()">
                            Reset (last 7 days)
                        </button>
                        <button type="submit" class="btn btn-primary">
                            <i class="bi bi-search me-1"></i> Search
                        </button>
                    </div>
                    @if (errorMessage()) {
                        <div class="col-12">
                            <div class="alert alert-danger py-2 mb-0" role="alert">
                                <i class="bi bi-exclamation-circle-fill me-2"></i>{{ errorMessage() }}
                            </div>
                        </div>
                    }
                </div>
            </form>
        </div>

        @if (failedLists.length > 0) {
            <div class="mx-3 mb-2 alert alert-warning py-2" role="alert">
                <i class="bi bi-exclamation-triangle-fill me-2"></i>
                Some filter lists could not be loaded ({{ failedLists.join(", ") }}), so those filters are empty. Reload the page to try again.
            </div>
        }

        <div class="mx-3 small text-body-secondary">
            <i class="bi bi-info-circle me-1"></i>
            Only changes made in the admin site and the platform are logged. Bulk master-data loads, dbt, and changes made directly in Snowsight are not.
        </div>

        @if (note()) {
            <div class="mx-3 mt-2 alert alert-warning py-2" role="status">
                <i class="bi bi-exclamation-triangle-fill me-2"></i>{{ note() }}
            </div>
        }

        <div class="mx-3 mt-2 d-flex justify-content-between align-items-center">
            <span class="text-body-secondary">{{ loading() ? "" : totalText(items().length, totalCount()) }}</span>
            @if (items().length > 0) {
                <button type="button" class="btn btn-sm btn-outline-primary" (click)="exportCsv()">
                    <i class="bi bi-download me-1"></i> Export CSV
                </button>
            }
        </div>

        <div class="mx-3 mt-2">
            @if (loading()) {
                <div class="d-flex justify-content-center align-items-center gap-2 p-5 text-body-secondary" role="status">
                    <span class="spinner-border text-primary"></span>
                    <span>Searching the audit log...</span>
                </div>
            } @else if (items().length > 0) {
                <div class="table-responsive">
                    <table class="table table-sm table-hover align-middle">
                        <thead>
                        <tr>
                            <th scope="col" sortable="createdAt" (sort)="onSort($event)">When (US Eastern)</th>
                            <th scope="col" sortable="userName" (sort)="onSort($event)">Who</th>
                            <th scope="col" sortable="customerName" (sort)="onSort($event)">Customer</th>
                            <th scope="col" sortable="tableName" (sort)="onSort($event)">Table</th>
                            <th scope="col" sortable="record" (sort)="onSort($event)">Record</th>
                            <th scope="col" sortable="columnName" (sort)="onSort($event)">Column</th>
                            <th scope="col" sortable="oldValue" (sort)="onSort($event)">Old</th>
                            <th scope="col" sortable="newValue" (sort)="onSort($event)">New</th>
                        </tr>
                        </thead>
                        <tbody>
                            @for (item of pageItems(); track item.id) {
                                <tr>
                                    <td class="text-nowrap">{{ formatEasternDateTime(item.createdAt) }}</td>
                                    <td [title]="item.userEmail ?? ''">{{ item.userName }}</td>
                                    <td>{{ item.customerName }}</td>
                                    <td><code>{{ item.tableName }}</code></td>
                                    <td [class.fst-italic]="!item.recordName">{{ recordText(item) }}</td>
                                    <td><code>{{ item.columnName }}</code></td>
                                    <td class="value-cell">{{ item.oldValue }}</td>
                                    <td class="value-cell">{{ item.newValue }}</td>
                                </tr>
                            }
                        </tbody>
                    </table>
                </div>
            } @else if (!errorMessage()) {
                <div class="card text-center bg-warning-subtle border border-warning rounded p-3 m-5">
                    <div class="card-body">
                        <i class="bi bi-exclamation-triangle-fill me-2"></i>
                        @if (loadFailed()) {
                            The audit log could not be loaded.
                            <div><button type="button" (click)="search()" class="btn btn-primary mt-3">Try again</button></div>
                        } @else {
                            No changes match these filters.
                            <div><a href="#" (click)="reset(); $event.preventDefault()" class="btn btn-primary mt-3">Clear filters</a></div>
                        }
                    </div>
                </div>
            }
        </div>

        @if (!loading() && items().length > 0) {
            <app-table-pager class="mx-3" [total]="items().length" [(page)]="page" [(pageSize)]="pageSize" />
        }
    </div>
</div>
